import React, { useState, useEffect } from 'react';
import { db1 } from '../firebase/config';
import { collection, addDoc, setDoc, serverTimestamp, doc } from 'firebase/firestore';
import { Container, Form, Button, Alert, Spinner, Card, Image } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const BlogUploadPage = () => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    author: '',
    excerpt: '',
    tags: ''
  });
  const [blogImages, setBlogImages] = useState([]);
  const [featuredImage, setFeaturedImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [blogToken, setBlogToken] = useState('');
  const [isArabic, setIsArabic] = useState(true);
  const navigate = useNavigate();

  const translations = {
    'Blog Upload': 'رفع مقال',
    'Publish your blog post or news article': 'انشر مقالك أو خبرك',
    'Title *': 'العنوان *',
    'Enter your post title': 'أدخل عنوان المنشور',
    'Content *': 'المحتوى *',
    'Write your blog post content': 'اكتب محتوى مقالك',
    'Category *': 'الفئة *',
    'Select a category': 'اختر فئة',
    'Author *': 'المؤلف *',
    'Enter author name': 'أدخل اسم المؤلف',
    'Excerpt': 'ملخص',
    'Brief description of your post': 'وصف مختصر لمنشورك',
    'Tags': 'الكلمات الدلالية',
    'Comma-separated tags': 'كلمات دلالية مفصولة بفواصل',
    'Featured Image': 'الصورة الرئيسية',
    'Upload Featured Image': 'رفع الصورة الرئيسية',
    'Additional Images': 'صور إضافية',
    'Upload Images': 'رفع الصور',
    'Publish Post': 'نشر المنشور',
    'Publishing...': 'جاري النشر...',
    'Post Published Successfully!': 'تم نشر المنشور بنجاح!',
    'Your post has been published successfully.': 'تم نشر منشورك بنجاح.',
    'Your Post Access Link': 'رابط الوصول إلى منشورك',
    'Save this private link to access your post:': 'احفظ هذا الرابط الخاص للوصول إلى منشورك:',
    'Copy Link': 'نسخ الرابط',
    'Go to Post': 'الانتقال إلى المنشور',
    'Please fill all required fields': 'يرجى ملء جميع الحقول المطلوبة',
    'Please add a featured image': 'يرجى إضافة صورة رئيسية',
    'Error saving post:': 'خطأ في حفظ المنشور:',
    'Upload widget is still loading. Please try again.': 'أداة التحميل لا تزال تحمّل. يرجى المحاولة مرة أخرى.',
    'News': 'أخبار',
    'Technology': 'تكنولوجيا',
    'Education': 'تعليم',
    'Health': 'صحة',
    'Politics': 'سياسة',
    'Culture': 'ثقافة',
    'Sports': 'رياضة',
    'Other': 'أخرى'
  };

  const translateText = (text) => {
    return isArabic ? (translations[text] || text) : text;
  };

  const toggleLanguage = () => {
    setIsArabic(!isArabic);
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    script.onload = () => setWidgetLoaded(true);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Add sample data function
  const addSampleData = () => {
    setFormData({
      title: 'Announcement: Anonymous Donations System (Sadaqah al-Sirr)',
      content: `In the Name of Allah, the Most Gracious, the Most Merciful  
  
To our respected brothers and sisters across the Ummah,  
  
As part of the Al-Majd Initiative—a public service launched by us to uphold the dignity and rights of our people—we are announcing the implementation of a system for Anonymous Donations (Sadaqah al-Sirr).  
  
This system is designed to fulfill a core obligation of our faith: to protect the honor of our brothers and sisters in times of need. Those who are in genuine hardship may contact the administration directly. Upon confidential verification, they will receive support.  
  
In accordance with the Sunnah of our Prophet (ﷺ), who taught us to conceal our charity, the identities and details of the recipients will never be published. This protects their dignity and ensures their need remains a matter between them, the administration, and Allah.  
  
We now call upon those whom Allah has blessed with wealth to fulfill this duty. Your donation is not just aid; it is a Sadaqah Jariyah (ongoing charity) that protects a Muslim's honor and helps restore their standing.  
  
Donate today. Let your left hand not know what your right hand gives. Help us restore dignity, one hidden act of kindness at a time.  
  
With brotherhood and responsibility,  
  
Your Brothers,  
The Soldiers of Syria  
Al-Majd Initiative`,
      category: 'news',
      author: 'Al-Majd Initiative',
      excerpt: 'Announcing the implementation of Anonymous Donations System (Sadaqah al-Sirr) to protect the dignity of those in need.',
      tags: 'donation, charity, sadaqah, help, support, muslim, ummah'
    });
    setFeaturedImage('/notice.png');
  };

  const openCloudinaryWidget = (isFeatured = false) => {
    if (!widgetLoaded) {
      setError(translateText("Upload widget is still loading. Please try again."));
      return;
    }

    window.cloudinary.openUploadWidget(
      {
        cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'drl2kb984',
        uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'salafcore',
        sources: ['local', 'url', 'camera'],
        multiple: !isFeatured,
        clientAllowedFormats: ['jpg', 'jpeg', 'png'],
        maxImageFileSize: 5000000,
        maxFiles: isFeatured ? 1 : 5
      },
      (error, result) => {
        if (!error && result && result.event === 'success') {
          if (isFeatured) {
            setFeaturedImage(result.info.secure_url);
          } else {
            setBlogImages(prev => [...prev, result.info.secure_url]);
          }
        }
      }
    );
  };

  const removeImage = (index, isFeatured = false) => {
    if (isFeatured) {
      setFeaturedImage('');
    } else {
      setBlogImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.title || !formData.content || !formData.category || !formData.author) {
      setError(translateText('Please fill all required fields'));
      setLoading(false);
      return;
    }

    if (!featuredImage) {
      setError(translateText('Please add a featured image'));
      setLoading(false);
      return;
    }

    try {
      const token = uuidv4();
      setBlogToken(token);

      const blogData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        featuredImage,
        additionalImages: blogImages,
        token: token,
        status: 'published',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        views: 0,
        likes: 0
      };

      const blogRef = await addDoc(collection(db1, 'blog_posts'), blogData);

      await setDoc(doc(db1, 'blog_tokens', token), {
        postId: blogRef.id,
        title: formData.title,
        createdAt: serverTimestamp(),
        status: 'published'
      });

      setSuccess(true);
    } catch (err) {
      setError(`${translateText('Error saving post:')} ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="my-5" style={{ 
      maxWidth: '800px',
      padding: '20px',
      direction: isArabic ? 'rtl' : 'ltr'
    }}>
      {/* Language Toggle Button */}
      <div className="d-flex justify-content-end mb-3">
        <Button 
          onClick={toggleLanguage}
          variant="outline-primary"
          style={{
            borderColor: '#164B31',
            color: '#164B31',
            borderRadius: '20px',
            padding: '5px 15px',
            fontWeight: '500'
          }}
        >
          {isArabic ? 'English' : 'العربية'}
        </Button>
      </div>

      <Card className="shadow-lg" style={{
        border: 'none',
        borderRadius: '12px',
        backgroundColor: '#FFFFFF',
        boxShadow: `
          0 0 0 1px rgba(255,255,255,0.9),
          0 2px 4px rgba(0,0,0,0.05),
          0 8px 16px rgba(0,0,0,0.1),
          inset 0 0 0 1px rgba(255,255,255,0.5),
          inset 0 -2px 0 rgba(255,255,255,0.5)
        `,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Card.Body style={{ padding: '30px' }}>
          <div className="text-center mb-4">
            <h2 style={{ color: '#164B31', fontWeight: '700' }}>
              {translateText('Blog Upload')}
            </h2>
            <p style={{ color: '#666' }}>
              {translateText('Publish your blog post or news article')}
            </p>
            
            {/* Sample Data Button */}
            <Button 
              variant="outline-secondary" 
              onClick={addSampleData}
              size="sm"
              className="mt-2"
              style={{ borderRadius: '20px' }}
            >
              📝 Load Sample Data
            </Button>
          </div>

          {success ? (
            <div>
              <Alert variant="light" style={{ 
                border: 'none',
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                color: '#164B31'
              }}>
                <div className="d-flex align-items-center">
                  <div className="rounded p-2 me-3" style={{ 
                    backgroundColor: '#164B31' 
                  }}>
                    <i className="bi bi-check-circle-fill text-light fs-4"></i>
                  </div>
                  <div>
                    <h4 style={{ color: '#164B31' }}>{translateText('Post Published Successfully!')}</h4>
                    <p style={{ color: '#164B31' }}>{translateText('Your post has been published successfully.')}</p>
                  </div>
                </div>
              </Alert>
              
              <Card className="mt-4" style={{ 
                border: 'none',
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <Card.Body>
                  <h5 className="mb-3" style={{ color: '#164B31' }}>
                    <i className="bi bi-link-45deg me-2"></i>
                    {translateText('Your Post Access Link')}
                  </h5>
                  <p style={{ color: '#164B31' }}>{translateText('Save this private link to access your post:')}</p>
                  <div className="rounded p-3 mb-3" style={{ 
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                  }}>
                    <code style={{ color: '#164B31', wordBreak: 'break-all' }}>
                      {window.location.origin}/blog/{blogToken}
                    </code>
                  </div>
                  <div className="d-flex gap-3 flex-wrap">
                    <Button 
                      variant="outline-primary" 
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/blog/${blogToken}`);
                        alert('Link copied to clipboard!');
                      }}
                      className="d-flex align-items-center"
                      style={{ 
                        color: '#164B31', 
                        borderColor: '#164B31',
                        borderRadius: '8px'
                      }}
                    >
                      <i className="bi bi-clipboard-fill me-2"></i>
                      {translateText('Copy Link')}
                    </Button>
                    <Button 
                      variant="primary"
                      onClick={() => navigate(`/blog/${blogToken}`)}
                      className="d-flex align-items-center"
                      style={{ 
                        backgroundColor: '#164B31', 
                        borderColor: '#164B31',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(22,75,49,0.2)'
                      }}
                    >
                      <i className="bi bi-box-arrow-in-right me-2"></i>
                      {translateText('Go to Post')}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          ) : (
            <Form onSubmit={handleSubmit}>
              {error && (
                <Alert variant="light" style={{ 
                  border: 'none',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  color: '#164B31'
                }}>
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                </Alert>
              )}

              <Form.Group className="mb-4">
                <Form.Label style={{ color: '#164B31', fontWeight: '600' }}>{translateText('Title *')}</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  placeholder={translateText('Enter your post title')}
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    color: '#164B31',
                    borderRadius: '8px',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                    padding: '10px 15px'
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ color: '#164B31', fontWeight: '600' }}>{translateText('Content *')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={12}
                  name="content"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  required
                  placeholder={translateText('Write your blog post content')}
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    color: '#164B31',
                    borderRadius: '8px',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                    padding: '10px 15px',
                    resize: 'none',
                    fontFamily: 'inherit'
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ color: '#164B31', fontWeight: '600' }}>{translateText('Category *')}</Form.Label>
                <Form.Select
                  name="category"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  required
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    color: '#164B31',
                    borderRadius: '8px',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                    padding: '10px 15px'
                  }}
                >
                  <option value="">{translateText('Select a category')}</option>
                  <option value="news">{translateText('News')}</option>
                  <option value="technology">{translateText('Technology')}</option>
                  <option value="education">{translateText('Education')}</option>
                  <option value="health">{translateText('Health')}</option>
                  <option value="politics">{translateText('Politics')}</option>
                  <option value="culture">{translateText('Culture')}</option>
                  <option value="sports">{translateText('Sports')}</option>
                  <option value="other">{translateText('Other')}</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ color: '#164B31', fontWeight: '600' }}>{translateText('Author *')}</Form.Label>
                <Form.Control
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={(e) => setFormData({...formData, author: e.target.value})}
                  required
                  placeholder={translateText('Enter author name')}
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    color: '#164B31',
                    borderRadius: '8px',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                    padding: '10px 15px'
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ color: '#164B31', fontWeight: '600' }}>{translateText('Excerpt')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  placeholder={translateText('Brief description of your post')}
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    color: '#164B31',
                    borderRadius: '8px',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                    padding: '10px 15px',
                    resize: 'none'
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ color: '#164B31', fontWeight: '600' }}>{translateText('Tags')}</Form.Label>
                <Form.Control
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({...formData, tags: e.target.value})}
                  placeholder={translateText('Comma-separated tags')}
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    color: '#164B31',
                    borderRadius: '8px',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                    padding: '10px 15px'
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ color: '#164B31', fontWeight: '600' }}>{translateText('Featured Image *')}</Form.Label>
                <div className="mb-3">
                  <Button 
                    variant="outline-primary" 
                    onClick={() => openCloudinaryWidget(true)}
                    type="button"
                    className="d-flex align-items-center me-2"
                    style={{ 
                      color: '#164B31', 
                      borderColor: '#164B31',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      backgroundColor: 'rgba(255,255,255,0.9)'
                    }}
                  >
                    <i className="bi bi-cloud-arrow-up-fill me-2"></i>
                    {translateText('Upload Featured Image')}
                  </Button>
                  <small style={{ color: '#666' }}>
                    Or use: <code>/notice.png</code>
                  </small>
                </div>
                
                {featuredImage && (
                  <div className="position-relative" style={{ maxWidth: '300px' }}>
                    <Image 
                      src={featuredImage} 
                      thumbnail
                      style={{ 
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover', 
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                    />
                    <Button
                      variant="light"
                      size="sm"
                      className="position-absolute top-0 end-0 p-0"
                      style={{ 
                        width: '24px', 
                        height: '24px',
                        borderRadius: '50%',
                        border: '1px solid rgba(0,0,0,0.1)',
                        color: '#164B31',
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                      onClick={() => removeImage(0, true)}
                    >
                      ×
                    </Button>
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ color: '#164B31', fontWeight: '600' }}>{translateText('Additional Images')}</Form.Label>
                <div className="mb-3">
                  <Button 
                    variant="outline-primary" 
                    onClick={() => openCloudinaryWidget(false)}
                    type="button"
                    className="d-flex align-items-center"
                    style={{ 
                      color: '#164B31', 
                      borderColor: '#164B31',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      backgroundColor: 'rgba(255,255,255,0.9)'
                    }}
                  >
                    <i className="bi bi-cloud-arrow-up-fill me-2"></i>
                    {translateText('Upload Images')}
                  </Button>
                </div>
                
                <div className="d-flex flex-wrap gap-3 mb-3">
                  {blogImages.map((img, index) => (
                    <div key={index} className="position-relative" style={{ width: '120px' }}>
                      <Image 
                        src={img} 
                        thumbnail
                        style={{ 
                          height: '120px', 
                          objectFit: 'cover', 
                          border: '1px solid rgba(0,0,0,0.1)',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}
                      />
                      <Button
                        variant="light"
                        size="sm"
                        className="position-absolute top-0 end-0 p-0"
                        style={{ 
                          width: '24px', 
                          height: '24px',
                          borderRadius: '50%',
                          border: '1px solid rgba(0,0,0,0.1)',
                          color: '#164B31',
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}
                        onClick={() => removeImage(index)}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </Form.Group>

              <Button 
                variant="primary" 
                type="submit" 
                disabled={loading}
                className="w-100 py-3"
                style={{ 
                  backgroundColor: '#164B31', 
                  border: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(22,75,49,0.2)',
                  transition: 'all 0.2s ease'
                }}
              >
                {loading ? (
                  <>
                    <Spinner as="span" size="sm" animation="border" className="me-2" />
                    {translateText('Publishing...')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-send-fill me-2"></i>
                    {translateText('Publish Post')}
                  </>
                )}
              </Button>
            </Form>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default BlogUploadPage;
